import Hls from 'hls.js';
import Player from 'video.js/dist/types/player';
import { HlsJsPreset } from '../../types';

/**
 * Seconds behind the live edge to keep playback.
 * The backend serves ~1s byte-range fragments carved from 30s source segments;
 * the newest fragments can briefly have no media yet, so we stay back from the edge.
 */
const LIVE_EDGE_OFFSET = 30;

/**
 * Live stream preset for byte-range HLS playlists.
 * Relies on hls.js native live handling (auto start + live sync); no custom
 * playlist polling or live-edge seeking is needed.
 */
export const livePreset: HlsJsPreset = {
  config: {
    enableWorker: true, // keep main thread snappy
    liveDurationInfinity: true,
    liveSyncDuration: LIVE_EDGE_OFFSET,
    liveMaxLatencyDuration: LIVE_EDGE_OFFSET * 3,
    backBufferLength: 0,
  },
  setup: (hls: Hls, player: Player) => {
    let intendedMuted: boolean | null = null;
    let autoplaySucceeded = false;

    /**
     * Video.js autoplay does not fire when hls.js owns the media element.
     * Attempt play as early as possible (setup, player ready, media attached) so
     * user activation from navigation is still valid. Fall back to muted play only
     * when the player was configured muted (e.g. GlobalLivePlayer).
     */
    const tryAutoplay = () => {
      if (autoplaySucceeded || !player.autoplay() || !player.paused()) {
        return;
      }

      if (intendedMuted === null) {
        intendedMuted = player.muted() ?? false;
      }

      const attemptPlay = () => {
        const playResult = player.play();
        if (!playResult || typeof (playResult as Promise<void>).then !== 'function') {
          return;
        }
        return playResult as Promise<void>;
      };

      attemptPlay()
        ?.then(() => {
          autoplaySucceeded = true;
        })
        ?.catch((err: Error) => {
          if (err.name !== 'NotAllowedError' || intendedMuted) {
            return;
          }
        });
    };

    tryAutoplay();
    player.ready(() => tryAutoplay());
    hls.on(Hls.Events.MEDIA_ATTACHED, () => tryAutoplay());
    hls.once(Hls.Events.MANIFEST_PARSED, () => tryAutoplay());
    hls.once(Hls.Events.FRAG_BUFFERED, () => tryAutoplay());
  },
};
